import React from "react";

import { App, Button, Modal, Input, Typography } from "antd";
import { useTranslation } from "react-i18next";

import { API_URL } from "../../shared/utils/Util";
import { CreateAnnouncementResponse, DeleteAnnouncementResponse } from "../../types/api";
import { fetchT } from "../../shared/utils/TypedUtil";
import { useAntdTheme } from "../../shared/hooks";

const { TextArea } = Input;
const { Title } = Typography;

type AnnouncementDeleteButtonProps = {
  restaurantId: string;
  alreadyShutdown: boolean;
  existingShutdownId?: string;
  onSuccess: () => void;
};

export const AnnouncementShutdownButton = (props: AnnouncementDeleteButtonProps) => {
  const { notification } = App.useApp();
  const { t } = useTranslation();
  const { token } = useAntdTheme();
  const [isModalOpen, setIsModalOpen] = React.useState<boolean>(false);
  const [confirmLoading, setConfirmLoading] = React.useState<boolean>(false);
  const [shutDownTitle, setShutdownTitle] = React.useState<string>(t("temporary_closure"));
  const [shutDownDetail, setShutdownDetail] = React.useState<string>(t("sorry_temporarily_closed"));

  const showModal = () => {
    setIsModalOpen(true);
  };

  const handleCreate = () => {
    setConfirmLoading(true);

    fetchT<CreateAnnouncementResponse>(
      "POST",
      `${API_URL}/v1/r/${props.restaurantId}/shut_down`,
      {
        title: shutDownTitle,
        content: shutDownDetail,
        annoucement_type: "SHUT_DOWN",
      },
      (result: CreateAnnouncementResponse) => {
        props.onSuccess();

        notification.success({
          message: t("success"),
          description: t("restaurant_shutdown_successfully"),
        });

        setConfirmLoading(false);
        setIsModalOpen(false);
      },
      (err: any) => {
        setConfirmLoading(false);
        setIsModalOpen(false);

        notification.error({
          message: t("error"),
          description: t("error_occurred"),
        });
      }
    );
  };

  const handleDelete = () => {
    setConfirmLoading(true);

    fetchT<DeleteAnnouncementResponse>(
      "POST",
      `${API_URL}/v1/r/${props.restaurantId}/re_open`,
      {},
      (result: DeleteAnnouncementResponse) => {
        props.onSuccess();

        notification.success({
          message: t("success"),
          description: t("restaurant_reopened_successfully"),
        });

        setConfirmLoading(false);
        setIsModalOpen(false);
      },
      (err: any) => {
        setConfirmLoading(false);
        setIsModalOpen(false);

        notification.error({
          message: t("error"),
          description: t("error_occurred"),
        });
      }
    );
  };

  const handleOk = () => {
    if (props.alreadyShutdown) {
      handleDelete();
      return;
    }
    handleCreate();
  };

  const handleCancel = () => {
    setIsModalOpen(false);
  };

  const handleTitleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setShutdownTitle(e.target.value);
  };

  const handleTextChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setShutdownDetail(e.target.value);
  };

  return (
    <>
      <Button
        type="primary"
        size="large"
        onClick={showModal}
        style={
          props.alreadyShutdown
            ? {
                border: "1px solid #7C9D1B",
                backgroundColor: "#7C9D1B",
                borderRadius: token.borderRadiusLG,
                height: token.controlHeightLG,
              }
            : {
                borderRadius: token.borderRadiusLG,
                height: token.controlHeightLG,
              }
        }
        danger
      >
        {props.alreadyShutdown ? t("open_restaurant") : t("shutdown_restaurant")}
      </Button>
      <p />
      <Modal
        title={
          <Title level={4} style={{ margin: 0 }}>
            {props.alreadyShutdown ? t("reopen") : t("temporarily_closed")}
          </Title>
        }
        open={isModalOpen}
        onOk={handleOk}
        onCancel={handleCancel}
        okText={t("ok")}
        cancelText={t("cancel")}
        okButtonProps={{
          loading: confirmLoading,
          style: {
            borderRadius: token.borderRadiusLG,
            height: token.controlHeight,
          },
        }}
        cancelButtonProps={{
          style: {
            borderRadius: token.borderRadiusLG,
            height: token.controlHeight,
          },
        }}
        styles={{
          body: { padding: token.paddingXL },
        }}
      >
        <p>{props.alreadyShutdown ? t("are_you_sure_reopen") : t("are_you_sure_shutdown")}</p>
        {!props.alreadyShutdown && (
          <>
            <Input
              maxLength={256}
              onChange={handleTitleChange}
              placeholder={t("title")}
              value={shutDownTitle}
              style={{
                borderRadius: token.borderRadiusLG,
                marginTop: token.marginMD,
              }}
            />
            <TextArea
              maxLength={4096}
              onChange={handleTextChange}
              placeholder={t("content")}
              value={shutDownDetail}
              rows={4}
              style={{
                borderRadius: token.borderRadiusLG,
                marginTop: token.marginMD,
              }}
            />
          </>
        )}
      </Modal>
    </>
  );
};
